'use client'

import { type IconName } from 'icons/types/IconName.types'
import { useEffect, useRef, useState } from 'react'
import type { ChangeEvent, FocusEvent, KeyboardEvent, MouseEvent } from 'react'
import { cx } from 'utils/cx'

import { Dropdown } from './Dropdown/Dropdown'
import { MobileSelect } from './MobileSelect/MobileSelect'
import { SelectIcon } from './SelectIcon/SelectIcon'
import { SelectMessage } from './SelectMessage/SelectMessage'
import { WrapperDropdown } from './WrapperDropdown/WrapperDropdown'

import styles from './Select.module.scss'
import texts from 'fukku/styles/texts.module.scss'

export interface OptionsType {
	value?: string | number
	text?: string | number | React.ReactNode
	disabled?: boolean
}

export interface SelectProps {
	readonly children?: React.ReactNode
	readonly id: string
	readonly name?: string
	readonly label: string
	readonly labelHidden?: boolean
	readonly className?: string
	readonly 'data-testid'?: string
	readonly disabled?: boolean
	readonly options: OptionsType[]
	readonly errorMessage?: string
	readonly errorPlaceholders?: Record<string, string | number>
	readonly hintMessage?: string
	readonly hintPlaceholders?: Record<string, string | number>
	readonly value?: string | number
	readonly onToggle?: (isOpen: boolean) => void
	readonly onBlur?: (e: FocusEvent) => void
	readonly onChange?: (value: string | number | null) => void
	readonly onFocus?: (e: FocusEvent) => void
	readonly icon?: IconName
	readonly iconShowCondition?: (option: OptionsType) => boolean
	readonly placeholder?: string
	readonly hasError?: boolean
	readonly isMobile?: boolean
}

export function Select(props: SelectProps): React.ReactNode {
	const {
		children,
		disabled = false,
		id,
		className = '',
		'data-testid': dataTestId,
		label,
		labelHidden,
		name = id,
		options,
		errorMessage,
		errorPlaceholders,
		hintMessage,
		hintPlaceholders,
		value,
		placeholder,
		hasError = false,
		onToggle = () => {
			/* does nothing */
		},
		onChange = () => {
			/* does nothing */
		},
		onFocus = () => {
			/* does nothing */
		},
		onBlur = () => {
			/* does nothing */
		},
		icon,
		iconShowCondition = () => true,
		isMobile = false,
	} = props

	const refSelectWrapper = useRef<HTMLDivElement | null>(null)
	const [selectedOption, setSelectedOption] = useState<
		string | number | undefined
	>(value)
	const [selectedOptionText, setSelectedOptionText] = useState<
		string | number | React.ReactNode
	>('')
	const [focused, setFocused] = useState(false)
	const [showHint, setHint] = useState(false)
	const [open, setOpen] = useState(false)
	const [isFilled, setIsFilled] = useState(false)

	const refDropdown = useRef<HTMLDivElement | null>(null)
	const refWrapperListBox = useRef<HTMLDivElement | null>(null)

	useEffect(() => {
		if (options && options.length > 0) {
			const textSelected = options.find(
				(option) => option.value?.toString() === selectedOption?.toString()
			)

			if (textSelected) {
				setSelectedOptionText(textSelected?.text || textSelected?.value || '')
				setIsFilled(true)
			} else {
				setSelectedOptionText('')
				setIsFilled(false)
			}
		}
	}, [selectedOption, options])

	const handleBlur = (e: FocusEvent<HTMLDivElement | HTMLSelectElement>) => {
		if (
			!refDropdown?.current?.contains(e.relatedTarget as Node) &&
			!refWrapperListBox?.current?.contains(e.relatedTarget as Node)
		) {
			setFocused(false)
			onBlur(e)
		}
	}
	const handleChange = (valueSelected: string | number): void => {
		const item =
			options?.length > 0
				? options.find(
						(option) => option.value?.toString() === valueSelected?.toString()
					)
				: null
		setSelectedOption(item?.value ?? undefined)
		onChange(item?.value ?? null)
		setIsFilled(!!item?.value?.toString())
	}

	const handleChangeMobile = ({
		target,
	}: ChangeEvent<HTMLSelectElement>): void => {
		handleChange(target.value)
	}

	const handleChangeDropdown = (
		event: MouseEvent<HTMLDivElement> | KeyboardEvent<HTMLDivElement>
	): void => {
		const target = event.target as HTMLDivElement
		if (target.dataset?.value) {
			handleChange(target.dataset.value)
		}
	}
	const handleFocus = (e: FocusEvent<HTMLDivElement | HTMLSelectElement>) => {
		setFocused(true)
		if (hintMessage) {
			setHint(true)
		}
		onFocus(e)
	}
	const handleHover = () => {
		if (hintMessage) {
			setHint(true)
		}
	}

	useEffect(() => {
		setSelectedOption(value)
		setIsFilled(!!(value && value !== ''))
		if (!value || value === '') {
			setSelectedOptionText('')
		}
	}, [value])

	const closeDropdown = () => {
		// TODO: Remove this function and use the toggleOpen function instead
		if (open) {
			toggleOpen()
		}
	}

	const toggleOpen = () => {
		if (!disabled && options?.length > 0) {
			setOpen((prevOpen) => !prevOpen)
			onToggle(!open)
		}
	}

	const handleArrowDown = () => {
		if (!open) {
			toggleOpen()
		}

		if (!refDropdown || !refDropdown.current) {
			return
		}
		const firstChild = refDropdown?.current?.firstElementChild as HTMLElement
		firstChild.focus()
		if (refDropdown && refDropdown.current) {
			refDropdown.current.dataset.index = '1'
		}
	}

	return (
		<div className={cx(styles.select, className)} ref={refSelectWrapper}>
			<WrapperDropdown
				{...{
					id,
					dataTestId,
					refDropdown,
					refWrapperListBox,
					disabled,
					handleBlur,
					handleFocus,
					handleHover,
					hasError,
					errorMessage,
					styles,
					focused,
					isFilled,
					open,
					toggleOpen,
					handleArrowDown,
					closeDropdown,
					refSelectWrapper,
					isMobile,
				}}
			>
				<>
					<label
						id={`${id}.label`}
						className={cx(
							styles.label,
							isFilled ? cx(styles.filledLabel, texts.uppercaseS) : texts.bodyM
						)}
						htmlFor={id}
						hidden={labelHidden}
					>
						{label}
					</label>
					<SelectIcon isMobile={isMobile} disabled={disabled} open={open} />
					{children}
					{isMobile && (
						<MobileSelect
							disabled={disabled}
							id={id}
							dataTestId={dataTestId}
							name={name}
							options={options}
							errorMessage={errorMessage}
							showHint={showHint}
							selectedOption={selectedOption}
							handleChange={handleChangeMobile}
							handleFocus={handleFocus}
							handleBlur={handleBlur}
							handleHover={handleHover}
							placeholder={placeholder}
						>
							{children}
						</MobileSelect>
					)}
					{!isMobile && selectedOptionText && (
						<p
							className={cx(
								texts.inputText,
								styles.selected,
								children ? styles.selectorHidden : null,
								disabled && styles.disabled,
								labelHidden && styles.selectedLabelHidden
							)}
						>
							{selectedOptionText}
						</p>
					)}
				</>
			</WrapperDropdown>
			{!isMobile && (
				<Dropdown
					id={id}
					ref={refDropdown}
					options={options}
					onChange={handleChangeDropdown}
					icon={icon}
					iconShowCondition={iconShowCondition}
					value={selectedOption}
					closeDropdown={closeDropdown}
					open={open}
					dataTestId={dataTestId}
					placeholder={placeholder}
				/>
			)}
			<SelectMessage
				id={id}
				hintMessage={hintMessage}
				errorMessage={errorMessage}
				hintPlaceholders={hintPlaceholders}
				errorPlaceholders={errorPlaceholders}
				showHint={showHint}
			/>
		</div>
	)
}

export default Select
